import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { getPoStatusPresentation } from '../placeholders/purchaseOrderSummary';

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Stacked = ({ top, bottom, topColor, mutedColor, topWeight = 700 }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topWeight, fontSize: 12, color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? <div style={{ fontSize: 11, color: mutedColor || '#64748b', marginTop: 1 }}>{bottom}</div> : null}
  </div>
);

const statusPresentation = (statusRaw, isDarkMode) => getPoStatusPresentation(statusRaw, isDarkMode);

const TABLE_COLUMNS = [
  { key: 'OC', align: 'left', sortKey: 'po_code' },
  { key: 'Fecha', align: 'left', sortKey: 'order_date' },
  { key: 'Proveedor', align: 'left', sortKey: 'supplier_name' },
  { key: 'Tipo', align: 'left', sortKey: 'item_type' },
  { key: 'Código', align: 'left', sortKey: 'item_code' },
  { key: 'Item', align: 'left', sortKey: 'description' },
  { key: 'Detalle', align: 'left', sortKey: 'detail' },
  { key: 'Cant.', align: 'right', sortKey: 'quantity' },
  { key: 'Formato', align: 'left', sortKey: 'unit' },
  { key: 'P. unitario', align: 'right', sortKey: 'unit_price' },
  { key: 'Total', align: 'right', sortKey: 'line_total' },
  { key: 'Estado', align: 'center', sortKey: 'po_status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'po_code':
      return String(row.po_code || '').toLowerCase();
    case 'order_date':
      return row.order_date ? new Date(row.order_date).getTime() : 0;
    case 'supplier_name':
      return String(row.supplier_name || '').toLowerCase();
    case 'item_type':
      return String(row.item_type || '').toLowerCase();
    case 'item_code':
      return String(row.item_code || '').toLowerCase();
    case 'description':
      return String(row.description || '').toLowerCase();
    case 'detail':
      return String(row.detail || '').toLowerCase();
    case 'quantity':
      return Number(row.quantity || 0);
    case 'unit':
      return String(row.unit || '').toLowerCase();
    case 'unit_price':
      return Number(row.unit_price || 0);
    case 'line_total':
      return Number(row.line_total || 0);
    case 'po_status':
      return String(row.po_status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const PurchaseOrderItemsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Ítems de órdenes de compra',
  attached = false,
  hideChrome = false,
  onPoClick,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.po_status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.po_code,
          row.supplier_name,
          row.supplier_code,
          row.supplier_rut,
          row.item_type,
          row.item_code,
          row.description,
          row.detail,
          row.unit,
          row.buyer_name,
          row.po_status,
          row.document_type,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((row) => String(row.po_status || '').trim() === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(['order_date', 'quantity', 'unit_price', 'line_total'].includes(key) ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ítems`
        : `${rows.length} ítems`);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    minWidth: 140,
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderTop: attached ? 'none' : `1px solid ${border}`,
              borderRadius: attached ? '0 0 12px 12px' : 12,
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{tableTitle}</h3>
            <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
          </div>
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="OC, proveedor, código, descripción…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado OC</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando ítems…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay ítems de órdenes de compra para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const st = statusPresentation(row.po_status, isDarkMode);
                const currency = String(row.currency || 'CLP').toUpperCase();
                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: `1px solid ${border}`, background: bg }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = bg;
                    }}
                  >
                    <td style={{ padding: 0, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 40 }}>
                        <div
                          style={{
                            width: 3,
                            flexShrink: 0,
                            background: st.bar,
                            borderRadius: '0 2px 2px 0',
                          }}
                          title={st.label}
                        />
                        <div style={{ padding: `${padV}px ${padH}px`, display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => onPoClick?.(row)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              cursor: 'pointer',
                              color: '#2563eb',
                              fontWeight: 700,
                              fontSize: 12,
                              lineHeight: 1.2,
                              textAlign: 'left',
                            }}
                          >
                            {row.po_code || '—'}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
                      <Stacked
                        top={formatDate(row.order_date)}
                        bottom={row.buyer_name || ''}
                        topColor={text}
                        mutedColor={muted}
                        topWeight={600}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: 180 }}>
                      <Stacked
                        top={row.supplier_name}
                        bottom={row.supplier_rut || ''}
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, verticalAlign: 'middle' }}>
                      {row.item_type || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted, verticalAlign: 'middle' }}>
                      {row.item_code || '—'}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        fontSize: 12,
                        color: text,
                        fontWeight: 600,
                        verticalAlign: 'middle',
                        maxWidth: 220,
                      }}
                    >
                      {row.description || '—'}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        fontSize: 12,
                        color: muted,
                        verticalAlign: 'middle',
                        maxWidth: 220,
                      }}
                    >
                      {row.detail || '—'}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        fontSize: 12,
                        textAlign: 'right',
                        color: text,
                        verticalAlign: 'middle',
                      }}
                    >
                      {row.quantity ?? 0}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text, verticalAlign: 'middle' }}>
                      {row.unit || 'UN'}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        fontSize: 12,
                        textAlign: 'right',
                        color: text,
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatMoney(row.unit_price, currency)}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        fontSize: 12,
                        textAlign: 'right',
                        fontWeight: 700,
                        color: text,
                        verticalAlign: 'middle',
                      }}
                    >
                      {formatMoney(row.line_total, currency)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          background: st.bg,
                          color: st.color,
                          border: `1px solid ${st.border}`,
                        }}
                      >
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseOrderItemsTable;
